import React, { useState, useEffect } from 'react';
import { 
    Settings as SettingsIcon, 
    User, 
    FileText, 
    Trash2, 
    ArrowLeft,
    Mail,
    Calendar,
    Shield,
    ExternalLink
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import AccountDeletionModal from './AccountDeletionModal';

const SettingsPage = ({ onBack }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [tosInfo, setTosInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const auth = getAuth();
    const user = auth.currentUser;

    // Backend API URL
    const API_URL = process.env.REACT_APP_API_URL;

    // Fetch ToS acceptance info from backend
    useEffect(() => {
        const fetchTosInfo = async () => {
            if (!user) {
                setLoading(false);
                return;
            }
            
            try {
                const token = await user.getIdToken(true);
                const response = await fetch(`${API_URL}/api/tos/check`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setTosInfo(data);
                }
            } catch (error) {
                console.error('Error fetching ToS info:', error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchTosInfo();
    }, [user, API_URL]);

    const formattedDate = tosInfo?.accepted_at 
        ? new Date(tosInfo.accepted_at).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : 'Not available';

    const handleDeleteComplete = () => {
        // User will be automatically signed out by Firebase
        // The auth state change will redirect them to login
        setShowDeleteModal(false);
    };

    const handleViewTerms = () => {
        window.open(`${API_URL}/terms`, '_blank');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <button 
                        onClick={onBack}
                        className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-md hover:shadow-lg transition-all mb-4 font-semibold text-gray-700"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back
                    </button>
                    
                    <div className="bg-white rounded-3xl shadow-xl p-6">
                        <div className="flex items-center gap-4">
                            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 p-4 rounded-2xl">
                                <SettingsIcon className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">Settings</h1>
                                <p className="text-gray-600">Manage your account and preferences</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Account Information */}
                <div className="bg-white rounded-3xl shadow-xl p-6 mb-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <User className="w-6 h-6 text-blue-600" />
                        Account Information
                    </h2>
                    
                    <div className="space-y-4">
                        <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                            <User className="w-5 h-5 text-gray-600 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-gray-600">Name</p>
                                <p className="text-gray-800">{user?.displayName || 'Not provided'}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                            <Mail className="w-5 h-5 text-gray-600 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-gray-600">Email</p>
                                <p className="text-gray-800">{user?.email}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                            <Calendar className="w-5 h-5 text-gray-600 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-gray-600">Account Created</p>
                                <p className="text-gray-800">
                                    {user?.metadata?.creationTime 
                                        ? new Date(user.metadata.creationTime).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                          })
                                        : 'Not available'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Privacy & Legal */}
                <div className="bg-white rounded-3xl shadow-xl p-6 mb-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <Shield className="w-6 h-6 text-green-600" />
                        Privacy & Legal
                    </h2>
                    
                    <div className="space-y-3">
                        <button
                            onClick={handleViewTerms}
                            className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                        >
                            <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-gray-600" />
                                <div className="text-left">
                                    <p className="font-semibold text-gray-800">Terms of Service</p>
                                    <p className="text-sm text-gray-600">View the terms you agreed to</p>
                                </div>
                            </div>
                            <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                        </button>
                        <button
                            onClick={() => window.open(`${API_URL}/privacy`, '_blank')}
                            className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                        >
                            <div className="flex items-center gap-3">
                                <Shield className="w-5 h-5 text-gray-600" />
                                <div className="text-left">
                                    <p className="font-semibold text-gray-800">Privacy Policy</p>
                                    <p className="text-sm text-gray-600">Learn how we protect your data</p>
                                </div>
                            </div>
                            <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                        </button>
                        {loading ? (
                            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                                <div className="animate-pulse flex-1">
                                    <div className="h-4 bg-gray-300 rounded w-1/3 mb-2"></div>
                                    <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                                <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-blue-900">Terms Accepted</p>
                                    <p className="text-blue-800">{formattedDate}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Data & Account Management */}
                <div className="bg-white rounded-3xl shadow-xl p-6 mb-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <Trash2 className="w-6 h-6 text-red-600" />
                        Data & Account Management
                    </h2>
                    
                    <div className="space-y-4">
                        <div className="p-4 bg-gray-50 rounded-xl">
                            <p className="text-sm text-gray-600 mb-2">
                                <strong>Your Rights:</strong> Under GDPR and CCPA, you have the right to:
                            </p>
                            <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
                                <li>Access your personal data</li>
                                <li>Request data deletion</li>
                                <li>Export your data</li>
                            </ul>
                        </div>

                        <div className="p-4 bg-red-50 rounded-xl border-2 border-red-200">
                            <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2">
                                <Trash2 className="w-5 h-5" />
                                Delete Account
                            </h3>
                            <p className="text-sm text-red-800 mb-4">
                                Permanently delete your account and all associated data. This action cannot be undone.
                            </p>
                            <button
                                onClick={() => setShowDeleteModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 hover:shadow-lg transition-all"
                            >
                                <Trash2 className="w-4 h-4" />
                                Delete My Account
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer Info */}
                <div className="text-center text-sm text-gray-500 pb-6">
                    <p>For questions or concerns, please email to woundostomyprep@gmail.com</p>
                </div>
            </div>

            {/* Account Deletion Modal */}
            <AccountDeletionModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onDeleteComplete={handleDeleteComplete}
            />
        </div>
    );
};

export default SettingsPage;
